/** Interactive piano keyboard component.
 *
 * Renders a 2-octave piano (C3–F4) with mouse/touch interaction
 * and visual highlighting of active notes.
 */

import { useCallback, useRef } from "react";
import { KEY_MAP } from "../lib/keymap.ts";
import { isBlackKey, noteName } from "../lib/noteUtils.ts";

/** Props for the Keyboard component. */
export type KeyboardProps = {
	readonly startNote: number;
	readonly endNote: number;
	readonly activeNotes: ReadonlySet<number>;
	readonly octaveOffset: number;
	readonly onNoteOn: (note: number) => void;
	readonly onNoteOff: (note: number) => void;
};

/** Reverse lookup: MIDI note → keyboard key label. */
function getKeyLabel(note: number, octaveOffset: number): string | undefined {
	const adjustedNote = note - octaveOffset * 12;
	for (const [key, mappedNote] of Object.entries(KEY_MAP)) {
		if (mappedNote === adjustedNote) {
			return key === ";" ? ";" : key === "'" ? "'" : key.toUpperCase();
		}
	}
	return undefined;
}

export function Keyboard({
	startNote,
	endNote,
	activeNotes,
	octaveOffset,
	onNoteOn,
	onNoteOff,
}: KeyboardProps) {
	const isMouseDownRef = useRef(false);
	const currentNoteRef = useRef<number | null>(null);

	const handleNoteStart = useCallback(
		(note: number) => {
			onNoteOn(note);
			currentNoteRef.current = note;
		},
		[onNoteOn],
	);

	const handleNoteEnd = useCallback(
		(note: number) => {
			onNoteOff(note);
			if (currentNoteRef.current === note) {
				currentNoteRef.current = null;
			}
		},
		[onNoteOff],
	);

	const handleMouseDown = useCallback(
		(note: number) => {
			isMouseDownRef.current = true;
			handleNoteStart(note);
		},
		[handleNoteStart],
	);

	const handleMouseUp = useCallback(() => {
		isMouseDownRef.current = false;
		if (currentNoteRef.current !== null) {
			handleNoteEnd(currentNoteRef.current);
		}
	}, [handleNoteEnd]);

	const handleMouseEnter = useCallback(
		(note: number) => {
			if (isMouseDownRef.current) {
				// Glissando: end previous note, start new one
				if (
					currentNoteRef.current !== null &&
					currentNoteRef.current !== note
				) {
					handleNoteEnd(currentNoteRef.current);
				}
				handleNoteStart(note);
			}
		},
		[handleNoteStart, handleNoteEnd],
	);

	const handleMouseLeave = useCallback(
		(note: number) => {
			if (isMouseDownRef.current && currentNoteRef.current === note) {
				handleNoteEnd(note);
			}
		},
		[handleNoteEnd],
	);

	// Build the list of notes
	const notes: number[] = [];
	for (let n = startNote; n <= endNote; n++) {
		notes.push(n);
	}

	const whiteNotes = notes.filter((n) => !isBlackKey(n));
	const blackNotes = notes.filter((n) => isBlackKey(n));

	// Calculate black key positions relative to white keys
	const getBlackKeyOffset = (note: number): number => {
		const pc = note % 12;
		// Find the white key index to the left
		const whiteIndex = whiteNotes.findIndex((wn) => wn > note) - 1;
		if (whiteIndex < 0) return 0;

		// Offset based on position within the group
		const offsets: Record<number, number> = {
			1: 0.6, // C#
			3: 1.6, // D#
			6: 3.6, // F#
			8: 4.6, // G#
			10: 5.6, // A#
		};

		const octaveStart = note - pc;
		const cIndex = whiteNotes.indexOf(octaveStart);
		if (cIndex === -1) {
			// Fallback: position between the two surrounding white keys
			return whiteIndex + 0.6;
		}

		return cIndex + (offsets[pc] ?? whiteIndex + 0.6);
	};

	const whiteKeyWidth = 100 / whiteNotes.length;

	return (
		<fieldset
			className="keyboard"
			onMouseUp={handleMouseUp}
			onMouseLeave={handleMouseUp}
			aria-label="Piano keyboard"
		>
			<div className="keyboard-keys">
				{/* White keys */}
				{whiteNotes.map((note) => {
					const isActive = activeNotes.has(note);
					const keyLabel = getKeyLabel(note, octaveOffset);
					return (
						<button
							key={note}
							type="button"
							className={`key white-key ${isActive ? "active" : ""}`}
							style={{ width: `${whiteKeyWidth}%` }}
							onMouseDown={() => handleMouseDown(note)}
							onMouseEnter={() => handleMouseEnter(note)}
							onMouseLeave={() => handleMouseLeave(note)}
							onTouchStart={(e) => {
								e.preventDefault();
								handleNoteStart(note);
							}}
							onTouchEnd={(e) => {
								e.preventDefault();
								handleNoteEnd(note);
							}}
							aria-label={noteName(note)}
							aria-pressed={isActive}
						>
							<span className="key-note-name">{noteName(note)}</span>
							{keyLabel && <span className="key-binding">{keyLabel}</span>}
						</button>
					);
				})}

				{/* Black keys (absolute positioned) */}
				{blackNotes.map((note) => {
					const isActive = activeNotes.has(note);
					const keyLabel = getKeyLabel(note, octaveOffset);
					const offset = getBlackKeyOffset(note);
					const left = (offset / whiteNotes.length) * 100;

					return (
						<button
							key={note}
							type="button"
							className={`key black-key ${isActive ? "active" : ""}`}
							style={{
								left: `${left}%`,
								width: `${whiteKeyWidth * 0.65}%`,
							}}
							onMouseDown={(e) => {
								e.stopPropagation();
								handleMouseDown(note);
							}}
							onMouseEnter={() => handleMouseEnter(note)}
							onMouseLeave={() => handleMouseLeave(note)}
							onTouchStart={(e) => {
								e.preventDefault();
								handleNoteStart(note);
							}}
							onTouchEnd={(e) => {
								e.preventDefault();
								handleNoteEnd(note);
							}}
							aria-label={noteName(note)}
							aria-pressed={isActive}
						>
							{keyLabel && <span className="key-binding">{keyLabel}</span>}
						</button>
					);
				})}
			</div>
		</fieldset>
	);
}
